import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import {
  CalendarClock,
  CheckCircle2,
  CircleHelp,
  ClipboardList,
  DollarSign,
  Hammer,
  Link2,
  MessageSquareText,
  Pencil,
  PinOff,
  Ruler,
  Save,
  ShieldAlert,
  Sparkles,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/cn';
import { useMediaPins } from '@/hooks/useMediaPins';
import { shortDate } from '@/lib/format';
import type {
  RenovationItemDto,
  RenovationMediaPinDto,
  RenovationPinCategory,
  RenovationPinStatus,
} from '@cc/types';

/**
 * Category metadata. Drives pin colour, the icon, and labels everywhere.
 * Keep the colour set distinct so a glance at the image tells you the mix of
 * notes / questions / defects / changes.
 */
const CATEGORY_META: Record<
  RenovationPinCategory,
  { label: string; icon: typeof MessageSquareText; bg: string; ring: string }
> = {
  NOTE: {
    label: 'Note',
    icon: MessageSquareText,
    bg: 'bg-slate-500',
    ring: 'ring-slate-300/60',
  },
  ITEM: {
    label: 'Item',
    icon: ClipboardList,
    bg: 'bg-teal-500',
    ring: 'ring-teal-300/60',
  },
  QUESTION: {
    label: 'Question',
    icon: CircleHelp,
    bg: 'bg-sky-500',
    ring: 'ring-sky-300/60',
  },
  DECISION: {
    label: 'Decision',
    icon: Sparkles,
    bg: 'bg-violet-500',
    ring: 'ring-violet-300/60',
  },
  DEFECT: {
    label: 'Defect',
    icon: ShieldAlert,
    bg: 'bg-rose-600',
    ring: 'ring-rose-300/60',
  },
  CHANGE: {
    label: 'Change order',
    icon: Hammer,
    bg: 'bg-amber-500',
    ring: 'ring-amber-300/60',
  },
  MEASUREMENT: {
    label: 'Measurement',
    icon: Ruler,
    bg: 'bg-emerald-500',
    ring: 'ring-emerald-300/60',
  },
};

const CATEGORY_ORDER: RenovationPinCategory[] = [
  'NOTE',
  'ITEM',
  'QUESTION',
  'DECISION',
  'DEFECT',
  'CHANGE',
  'MEASUREMENT',
];

export interface HeroPinBoardHandle {
  /** Scroll the hero into view and briefly highlight the pin. */
  focusPin: (pinId: string) => void;
}

interface Props {
  projectId: string;
  roomId: string;
  mediaId: string;
  heroUrl: string;
  items: RenovationItemDto[];
  onUnpin: () => void;
}

/**
 * Pinnable hero image. Renders the room's pinned reference image; click any
 * empty spot to drop a numbered pin, write a comment, and optionally link
 * the pin to one of the room's items. Hover a pin for its comment + linked
 * row. Click an existing pin to edit or delete.
 *
 * Pin positions are stored as 0..1 fractions of the image's intrinsic size,
 * so they survive any rendered width.
 */
export const HeroPinBoard = forwardRef<HeroPinBoardHandle, Props>(function HeroPinBoard(
  { projectId, roomId, mediaId, heroUrl, items, onUnpin },
  ref,
) {
  const imgWrapRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [composer, setComposer] = useState<
    | { mode: 'new'; xNorm: number; yNorm: number }
    | { mode: 'view' | 'edit'; pin: RenovationMediaPinDto }
    | null
  >(null);
  const [imgError, setImgError] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  const { pins, create, update, remove } = useMediaPins({ projectId, roomId, mediaId });

  // Index pins by id and assign stable display numbers (oldest = 1).
  const numberedPins = useMemo(() => {
    return pins.map((p, i) => ({ ...p, index: i + 1 }));
  }, [pins]);

  useImperativeHandle(
    ref,
    () => ({
      focusPin: (pinId: string) => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedId(pinId);
        window.setTimeout(() => setHighlightedId((curr) => (curr === pinId ? null : curr)), 2000);
      },
    }),
    [],
  );

  // Click on empty area of the image → open the new-pin composer at that
  // relative position. We ignore clicks that originated on an existing pin
  // (handled below) or on the composer itself.
  function handleImageClick(e: ReactMouseEvent<HTMLDivElement>) {
    if (composer) return;
    const wrap = imgWrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const xNorm = clamp01((e.clientX - rect.left) / rect.width);
    const yNorm = clamp01((e.clientY - rect.top) / rect.height);
    setComposer({ mode: 'new', xNorm, yNorm });
  }

  if (imgError) return null;

  return (
    <Card ref={cardRef}>
      <CardContent className="relative p-0">
        <div
          ref={imgWrapRef}
          onClick={handleImageClick}
          className="relative cursor-crosshair select-none"
        >
          <img
            src={heroUrl}
            alt="Room reference"
            onError={() => setImgError(true)}
            className="max-h-[420px] w-full rounded-md object-contain"
            draggable={false}
            loading="lazy"
          />

          {numberedPins.map((p) => {
            const linkedItem = p.linkedItemId
              ? items.find((i) => i.id === p.linkedItemId)
              : null;
            const meta = CATEGORY_META[p.category];
            const isResolved = p.status === 'RESOLVED';
            const tooltipBits = [
              `${meta.label} #${p.index}${isResolved ? ' · resolved' : ''}`,
              linkedItem ? `Linked: ${linkedItem.activity}` : null,
              p.dueDate ? `Due ${p.dueDate}` : null,
              p.assignee ? `Assignee: ${p.assignee}` : null,
              p.comment,
            ].filter(Boolean);
            return (
              <button
                key={p.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setComposer({ mode: 'view', pin: p });
                }}
                className={cn(
                  'absolute z-10 flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-[11px] font-semibold text-white shadow-md transition-all hover:scale-110',
                  highlightedId === p.id
                    ? `bg-amber-500 ring-4 ring-amber-300/60`
                    : `${meta.bg} hover:${meta.bg}/90`,
                  isResolved && 'opacity-60 ring-2 ring-white/40',
                )}
                style={{ left: `${p.xNorm * 100}%`, top: `${p.yNorm * 100}%` }}
                title={tooltipBits.join('\n')}
              >
                {isResolved ? <CheckCircle2 className="h-3.5 w-3.5" /> : p.index}
              </button>
            );
          })}

          {composer && (
            <PinComposer
              key={
                composer.mode === 'new'
                  ? 'new'
                  : `${composer.mode}-${composer.pin.id}`
              }
              composer={composer}
              items={items}
              busy={create.isPending || update.isPending || remove.isPending}
              onClose={() => setComposer(null)}
              onEdit={
                composer.mode === 'view'
                  ? () => setComposer({ mode: 'edit', pin: composer.pin })
                  : undefined
              }
              onToggleStatus={
                composer.mode === 'view'
                  ? async () => {
                      const next: RenovationPinStatus =
                        composer.pin.status === 'OPEN' ? 'RESOLVED' : 'OPEN';
                      await update.mutateAsync({
                        pinId: composer.pin.id,
                        patch: { status: next },
                      });
                      setComposer(null);
                    }
                  : undefined
              }
              onSave={async (data) => {
                if (composer.mode === 'new') {
                  await create.mutateAsync({
                    xNorm: composer.xNorm,
                    yNorm: composer.yNorm,
                    ...data,
                  });
                } else {
                  await update.mutateAsync({
                    pinId: composer.pin.id,
                    patch: data,
                  });
                }
                setComposer(null);
              }}
              onDelete={
                composer.mode === 'view' || composer.mode === 'edit'
                  ? async () => {
                      await remove.mutateAsync(composer.pin.id);
                      setComposer(null);
                    }
                  : undefined
              }
            />
          )}
        </div>

        <div className="absolute right-2 top-2 z-20 flex items-center gap-1 rounded-md bg-background/80 px-1.5 py-1 backdrop-blur">
          <button
            onClick={onUnpin}
            className="text-[10px] text-muted-foreground hover:text-foreground"
            title="Unpin from hub"
          >
            <PinOff className="h-3 w-3" />
          </button>
        </div>
        <div className="border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground">
          Click anywhere on the image to drop a pin (leave a note, or link it to a work item). {pins.length > 0 && `· ${pins.length} pin${pins.length === 1 ? '' : 's'}`}
        </div>
      </CardContent>
    </Card>
  );
});

interface PinFormData {
  comment: string;
  linkedItemId: string | null;
  category: RenovationPinCategory;
  status: RenovationPinStatus;
  dueDate: string | null;
  costImpact: number | null;
  assignee: string | null;
}

interface PinComposerProps {
  composer:
    | { mode: 'new'; xNorm: number; yNorm: number }
    | { mode: 'view' | 'edit'; pin: RenovationMediaPinDto };
  items: RenovationItemDto[];
  busy: boolean;
  onClose: () => void;
  /** Only set in view mode; switches the popover into edit mode. */
  onEdit?: () => void;
  /** Only set in view mode; toggles OPEN ↔ RESOLVED. */
  onToggleStatus?: () => Promise<void>;
  onSave: (data: PinFormData) => Promise<void>;
  onDelete?: () => Promise<void>;
}

function PinComposer({
  composer,
  items,
  busy,
  onClose,
  onEdit,
  onToggleStatus,
  onSave,
  onDelete,
}: PinComposerProps) {
  const xNorm = composer.mode === 'new' ? composer.xNorm : composer.pin.xNorm;
  const yNorm = composer.mode === 'new' ? composer.yNorm : composer.pin.yNorm;
  const flipX = xNorm > 0.6;
  const flipY = yNorm > 0.6;

  const wrapperStyle: React.CSSProperties = {
    left: `${xNorm * 100}%`,
    top: `${yNorm * 100}%`,
    transform: `translate(${flipX ? '-100%' : '0'}, ${flipY ? '-100%' : '0'})`,
    marginLeft: flipX ? '-8px' : '12px',
    marginTop: flipY ? '-8px' : '12px',
  };

  if (composer.mode === 'view') {
    return (
      <PinViewer
        pin={composer.pin}
        items={items}
        busy={busy}
        wrapperStyle={wrapperStyle}
        onClose={onClose}
        onEdit={onEdit}
        onToggleStatus={onToggleStatus}
        onDelete={onDelete}
      />
    );
  }

  const editorComposer:
    | { mode: 'new'; xNorm: number; yNorm: number }
    | { mode: 'edit'; pin: RenovationMediaPinDto } =
    composer.mode === 'new'
      ? composer
      : { mode: 'edit', pin: composer.pin };

  return (
    <PinEditor
      composer={editorComposer}
      items={items}
      busy={busy}
      onClose={onClose}
      onSave={onSave}
      onDelete={onDelete}
      wrapperStyle={wrapperStyle}
    />
  );
}

function PinViewer({
  pin,
  items,
  busy,
  wrapperStyle,
  onClose,
  onEdit,
  onToggleStatus,
  onDelete,
}: {
  pin: RenovationMediaPinDto;
  items: RenovationItemDto[];
  busy: boolean;
  wrapperStyle: React.CSSProperties;
  onClose: () => void;
  onEdit?: () => void;
  onToggleStatus?: () => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const meta = CATEGORY_META[pin.category];
  const Icon = meta.icon;
  const linkedItem = pin.linkedItemId
    ? items.find((i) => i.id === pin.linkedItemId) ?? null
    : null;
  const isResolved = pin.status === 'RESOLVED';

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="absolute z-30 w-72 rounded-md border border-border bg-card text-card-foreground p-3 shadow-xl"
      style={wrapperStyle}
    >
      <div className="mb-2 flex items-center gap-1.5">
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white',
            meta.bg,
          )}
        >
          <Icon className="h-3 w-3" />
          {meta.label}
        </span>
        <Badge variant={isResolved ? 'success' : 'muted'} className="text-[10px]">
          {isResolved ? 'resolved' : 'open'}
        </Badge>
      </div>

      <p className="mb-3 whitespace-pre-wrap text-sm">{pin.comment}</p>

      <dl className="mb-3 space-y-1 text-[11px]">
        {linkedItem && (
          <Row icon={Link2} label="Linked">
            <span className="truncate">{linkedItem.activity}</span>
          </Row>
        )}
        {pin.assignee && (
          <Row icon={User} label="Assignee">
            {pin.assignee}
          </Row>
        )}
        {pin.dueDate && (
          <Row icon={CalendarClock} label="Due">
            {shortDate(pin.dueDate)}
          </Row>
        )}
        {pin.costImpact !== null && (
          <Row icon={DollarSign} label="Cost impact">
            ${pin.costImpact.toLocaleString()}
          </Row>
        )}
      </dl>

      <div className="flex items-center justify-end gap-1">
        {onDelete && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={onDelete}
            title="Delete"
            className="mr-auto h-7 px-2 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
        {onToggleStatus && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => void onToggleStatus()}
            className="h-7 px-2"
            title={isResolved ? 'Re-open' : 'Mark resolved'}
          >
            <CheckCircle2 className="mr-1 h-3 w-3" />
            {isResolved ? 'Re-open' : 'Resolve'}
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={onClose}
          className="h-7 px-2"
        >
          <X className="h-3 w-3" />
        </Button>
        {onEdit && (
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={onEdit}
            className="h-7 px-2"
          >
            <Pencil className="mr-1 h-3 w-3" /> Edit
          </Button>
        )}
      </div>
    </div>
  );
}

function Row({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof MessageSquareText;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icon className="h-3 w-3 flex-none" />
      <dt className="w-16 flex-none">{label}</dt>
      <dd className="flex-1 truncate text-foreground">{children}</dd>
    </div>
  );
}

interface PinEditorProps {
  composer:
    | { mode: 'new'; xNorm: number; yNorm: number }
    | { mode: 'edit'; pin: RenovationMediaPinDto };
  items: RenovationItemDto[];
  busy: boolean;
  onClose: () => void;
  onSave: (data: PinFormData) => Promise<void>;
  onDelete?: () => Promise<void>;
  wrapperStyle: React.CSSProperties;
}

function PinEditor({
  composer,
  items,
  busy,
  onClose,
  onSave,
  onDelete,
  wrapperStyle,
}: PinEditorProps) {
  const initial = composer.mode === 'edit' ? composer.pin : null;
  const [comment, setComment] = useState(initial?.comment ?? '');
  const [linkedItemId, setLinkedItemId] = useState<string | 'none'>(
    initial?.linkedItemId ?? 'none',
  );
  const [category, setCategory] = useState<RenovationPinCategory>(initial?.category ?? 'NOTE');
  const [dueDate, setDueDate] = useState<string>(initial?.dueDate ?? '');
  const [costImpact, setCostImpact] = useState<string>(
    initial?.costImpact !== null && initial?.costImpact !== undefined
      ? String(initial.costImpact)
      : '',
  );
  const [assignee, setAssignee] = useState<string>(initial?.assignee ?? '');

  useEffect(() => {
    const t = window.setTimeout(() => {
      const ta = document.getElementById('pin-composer-textarea') as HTMLTextAreaElement | null;
      ta?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, []);

  const placeholderByCategory: Record<RenovationPinCategory, string> = {
    NOTE: 'e.g. "nib wall continues here"',
    ITEM: 'e.g. "this is the kitchen pendant — see linked row"',
    QUESTION: 'e.g. "tradesman to confirm tap location"',
    DECISION: 'e.g. "hold off, waiting on tile pick"',
    DEFECT: 'e.g. "tile chip in corner"',
    CHANGE: 'e.g. "extend bench by 200mm"',
    MEASUREMENT: 'e.g. "outlet centre 1100mm AFL"',
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="absolute z-30 w-80 rounded-md border border-border bg-card text-card-foreground p-3 shadow-xl"
      style={wrapperStyle}
    >
      <Label>Category</Label>
      <Select value={category} onValueChange={(v) => setCategory(v as RenovationPinCategory)}>
        <SelectTrigger className="mb-2 h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CATEGORY_ORDER.map((c) => {
            const m = CATEGORY_META[c];
            const Icon = m.icon;
            return (
              <SelectItem key={c} value={c}>
                <div className="flex items-center gap-2">
                  <Icon className="h-3 w-3" />
                  {m.label}
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      <Label>Note</Label>
      <Textarea
        id="pin-composer-textarea"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={placeholderByCategory[category]}
        rows={3}
        className="mb-2 text-sm"
      />

      <Label>Link to a work item (optional)</Label>
      <Select value={linkedItemId} onValueChange={(v) => setLinkedItemId(v as string)}>
        <SelectTrigger className="mb-2 h-8 text-xs">
          <SelectValue placeholder="Leave unlinked" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— No work item —</SelectItem>
          {items.map((it) => (
            <SelectItem key={it.id} value={it.id}>
              {truncate(it.activity, 50)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="mb-2 grid grid-cols-2 gap-2">
        <div>
          <Label>Due date</Label>
          <Input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div>
          <Label>Cost impact ($)</Label>
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            value={costImpact}
            onChange={(e) => setCostImpact(e.target.value)}
            placeholder="0.00"
            className="h-8 text-xs"
          />
        </div>
      </div>

      <Label>Assignee (vendor / tradesperson)</Label>
      <Input
        value={assignee}
        onChange={(e) => setAssignee(e.target.value)}
        placeholder="e.g. Hunter Plumbing"
        className="mb-3 h-8 text-xs"
      />

      <div className="flex items-center justify-end gap-1">
        {onDelete && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={onDelete}
            title="Delete pin"
            className="mr-auto h-7 px-2 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={onClose}
          className="h-7 px-2"
        >
          <X className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={busy || !comment.trim()}
          onClick={() => {
            const costNum =
              costImpact.trim() === '' ? null : Number.parseFloat(costImpact);
            void onSave({
              comment: comment.trim(),
              linkedItemId: linkedItemId === 'none' ? null : linkedItemId,
              category,
              status: initial?.status ?? 'OPEN',
              dueDate: dueDate.trim() === '' ? null : dueDate,
              costImpact: costNum !== null && Number.isFinite(costNum) ? costNum : null,
              assignee: assignee.trim() === '' ? null : assignee.trim(),
            });
          }}
          className="h-7 px-2"
        >
          <Save className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
      {children}
    </p>
  );
}

function clamp01(n: number): number {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
